// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * Engine API — documents
 *
 * @remarks
 * Curated from `citec_engine` OpenAPI (`UserDocument*` / `Document*` schemas).
 *
 * - **Schema types:** {@link UserDocumentResponse}, {@link DocumentFileSummary},
 *   {@link DocumentFile}, {@link DocumentRelationResponse}
 * - **Endpoint types:** {@link UserDocumentsListResponse},
 *   {@link UserDocumentDetailResponse}, {@link UserDocumentUpdate}
 * - **Feature alias:** {@link Document} = {@link UserDocumentResponse}
 */

/** Whether a document belongs to the end user or their company. */
export enum DocumentScope {
  Personal = 'personal',
  Company = 'company',
  SilverAlpha = 'silver_alpha',
}

/** Entity type a document can be linked to (`USER_DOCUMENTS_RELATIONS`). */
export enum DocumentEntityType {
  IlliquidAssets = 'ILLIQUID_ASSETS',
  Position = 'POSITION',
  Transaction = 'TRANSACTION',
  Account = 'ACCOUNT',
  Debt = 'DEBT',
  CitecUser = 'CITEC_USER',
}

/** File metadata without download URL (list, create, update). */
export interface DocumentFileSummary {
  /** Original file name as uploaded. */
  file_name: string;

  /** Size in bytes. */
  file_size: number;
}

/** File with signed download URL (`GET /documents/{document_id}/` only). */
export interface DocumentFile extends DocumentFileSummary {
  /** Time-limited signed download URL. */
  url: string;
}

/** Relation payload when creating or updating document links. */
export interface DocumentRelationInput {
  entity_type: DocumentEntityType;

  /** Target entity identifier in the engine. */
  entity_id: string;
}

/** Stored relation returned in document responses. */
export interface DocumentRelationResponse {
  id: number;

  entity_type: DocumentEntityType;

  entity_id: string;

  /** ISO date-time when the relation was created. */
  created_at: string;
}

/**
 * Full document row returned by list, create, and update endpoints.
 *
 * @remarks
 * Monetary `amount` is stored as a string in responses. Use
 * {@link UserDocumentDetailResponse} when signed file URLs are required.
 */
export interface UserDocumentResponse {
  id: number;

  title: string;

  /** Attached files (metadata only; no URLs). */
  files?: DocumentFileSummary[];

  language?: string | null;

  category: string;

  subcategory: string;

  document_type: string;

  scope: DocumentScope;

  issuer?: string | null;

  /** Document effective date (ISO date) when applicable. */
  document_date?: string | null;

  expiry_date?: string | null;

  amount?: string | null;

  currency?: string | null;

  description?: string | null;

  user_id?: number | null;

  client_id?: number | null;

  uploaded_at: string;

  updated_at: string;

  relations?: DocumentRelationResponse[];
}

/** Parsed JSON shape for the multipart `metadata` field on document create. */
export type UserDocumentMetadata = Pick<
  UserDocumentResponse,
  'title' | 'category' | 'subcategory' | 'document_type' | 'scope'
> &
  Partial<
    Pick<
      UserDocumentResponse,
      | 'language'
      | 'issuer'
      | 'document_date'
      | 'expiry_date'
      | 'amount'
      | 'currency'
      | 'description'
      | 'client_id'
    >
  >;

/** Wrapper for `GET /documents/`. */
export interface UserDocumentsListResponse {
  documents: UserDocumentResponse[];
}

/** Signed download URLs for a document's files (`GET /documents/{document_id}/`). */
export interface UserDocumentDetailResponse {
  id: number;

  title: string;

  files: DocumentFile[];
}

/** Partial update body for `PUT /documents/{document_id}/`. */
export interface UserDocumentUpdate {
  title?: string | null;

  files?: DocumentFileSummary[] | null;

  language?: string | null;

  category?: string | null;

  subcategory?: string | null;

  document_type?: string | null;

  scope?: DocumentScope | null;

  issuer?: string | null;

  document_date?: string | null;

  expiry_date?: string | null;

  amount?: number | string | null;

  currency?: string | null;

  description?: string | null;

  relations_to_add?: DocumentRelationInput[] | null;

  relations_to_remove?: DocumentRelationInput[] | null;
}

/** Domain alias used by document-management UI and hooks. */
export type Document = UserDocumentResponse;

/** Query parameters for listing documents. */
export type ListDocumentsParams = void;

/** Request body for creating a document (`multipart/form-data`). */
export interface CreateDocumentRequestBody {
  /** Stringified {@link UserDocumentMetadata} JSON. */
  metadata: string;

  /** One or more files to attach. */
  files: File[] | Blob[];
}

/** Parameters for fetching signed download URLs for a document. */
export interface GetDocumentParams {
  document_id: number;
}

export type CreateDocumentMutationParams = {
  body: CreateDocumentRequestBody;
};

export interface UpdateDocumentParams {
  document_id: number;
}

export type UpdateDocumentMutationParams = UpdateDocumentParams & {
  body: UserDocumentUpdate;
};

export interface DeleteDocumentParams {
  document_id: number;

  /**
   * Force delete when the document is linked to entities (Engine `force` query
   * param).
   */
  force?: boolean;
}
